import { createLiveChartTooltip } from './live-chart-tooltip.js';

const TICK_ANIMATION = { duration: 500 };

// Live price label placement, in px.
const LABEL_GAP_RIGHT_OF_DOT = 9;
const LABEL_GAP_LEFT_OF_DOT = 8;
const LABEL_MAX_X_FROM_CHART_RIGHT = 30;
const TAIL_ANCHOR_MIN_PX_ABOVE_DOT = 2;
const LABEL_BASELINE_ABOVE_DOT = 7;
const LABEL_BASELINE_BELOW_DOT = 16;
const LABEL_MIN_BASELINE_Y_FROM_CHART_TOP = 12;

// The hidden scatter point that puts a contract's live price in the y-axis range.
export function liveChartSeries({ name, color, contractId, x, price, yAxis = 0, visible = true }) {
  return {
    type: 'scatter',
    name,
    data: [{ x, y: price }],
    yAxis,
    color,
    showInLegend: false,
    visible,
    enableMouseTracking: false,
    marker: { enabled: false },
    animation: false,
    includeInDataExport: false,
    custom: { chartLive: true, contractId },
    states: { hover: { enabled: false }, inactive: { enabled: false } },
  };
}

export function attachLiveChartOverlay(chart, { reducedMotion = false } = {}) {
  const highcharts = window.Highcharts;
  const host = chart.renderTo;
  const renderer = chart.renderer;
  const axis = chart.xAxis[0];
  const reservedLiveX = axis.options.custom?.liveChartHistoryLength;
  const hasReservedSlot = Number.isSafeInteger(reservedLiveX) && reservedLiveX >= 0;
  const originalCategories = hasReservedSlot
    ? axis.categories.slice(0, reservedLiveX)
    : [...axis.categories];
  const originalMax = axis.options.max;
  const entries = new Map();
  for (const series of chart.series) {
    if (series.type !== 'line' || series.options.contractId == null) continue;
    const contractId = Number(series.options.contractId);
    const anchor = [...series.data].reverse().find((point) => Number.isFinite(point.y));
    entries.set(contractId, { contractId, series, anchor, color: series.color, name: series.name });
  }
  const liveX = hasReservedSlot
    ? reservedLiveX
    : Math.max(
        originalCategories.length,
        ...[...entries.values()].map((e) => (e.anchor?.x ?? -1) + 1),
      );
  const uid = highcharts.uniqueKey();
  const group = renderer.g('chart-live-overlay').attr({ zIndex: 7 }).add();
  const guide = renderer
    .path()
    .attr({ stroke: 'var(--chart-axis-text, #8a9099)', 'stroke-width': 1 })
    .add(group)
    .hide();
  const tooltip = createLiveChartTooltip(host, `${uid}-tooltip`);
  const target = document.createElement('button');
  target.type = 'button';
  target.className = 'chart-live-target';
  target.setAttribute('aria-label', 'Show live prices');
  target.setAttribute('aria-describedby', tooltip.element.id);
  target.hidden = true;
  host.append(target);
  host.classList.add('chart-live-enabled');
  host.classList.toggle('chart-live-reduced-motion', reducedMotion);
  let disposed = false,
    destroying = false,
    axisExtended = hasReservedSlot;
  let hoveredContractId = null;
  let tooltipTop = null;
  let renderAnimation = false;
  let stopSweep = null;
  const pending = new Map();
  const animating = new Set(
    chart.series.filter(
      (series) =>
        series.visible &&
        !series.finishedAnimating &&
        highcharts.animObject(series.options.animation).duration > 0,
    ),
  );
  const cleanups = [];
  const activeEntries = () =>
    [...entries.values()].filter((entry) => entry.liveSeries && entry.series.visible);
  const alive = () => !disposed && !!chart.renderer;
  const motion = () =>
    !reducedMotion && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  function hideTooltip() {
    tooltip.element.hidden = true;
    guide.hide();
    updateHover();
  }
  function showTooltip() {
    if (!alive() || !activeEntries().length) return;
    chart.tooltip?.hide(0);
    tooltip.element.hidden = false;
    hoveredContractId = null;
    // Reset Highcharts' historical series highlight when entering the live area.
    chart.hoverSeries?.onMouseOut();
    guide.show();
    updateHover();
    tooltip.position(axis.toPixels(liveX), chart.plotTop + 8);
    tooltipTop = host.getBoundingClientRect().top;
  }
  function listen(element, event, callback, options) {
    element.addEventListener(event, callback, options);
    cleanups.push(() => element.removeEventListener(event, callback, options));
  }
  listen(target, 'pointerenter', showTooltip);
  listen(target, 'click', showTooltip);
  listen(target, 'focus', showTooltip);
  listen(target, 'blur', hideTooltip);
  listen(host, 'pointermove', (event) => {
    if (
      event.pointerType === 'mouse' &&
      !target.contains(event.target) &&
      !tooltip.element.contains(event.target)
    )
      hideTooltip();
  });
  listen(host, 'pointerleave', hideTooltip);
  listen(document, 'pointerdown', (event) => {
    if (!target.contains(event.target) && !tooltip.element.contains(event.target)) hideTooltip();
  });
  listen(document, 'keydown', (event) => {
    if (event.key === 'Escape') hideTooltip();
  });
  // Scrolling carries the chart away from an open tooltip, and under the sticky headers.
  listen(
    document,
    'scroll',
    () => {
      if (!tooltip.element.hidden && Math.abs(host.getBoundingClientRect().top - tooltipTop) > 2)
        hideTooltip();
    },
    { capture: true, passive: true },
  );
  let inView = true;
  function updateMotion() {
    host.classList.toggle('chart-live-paused', document.hidden || !inView);
  }
  listen(document, 'visibilitychange', updateMotion);
  const observer = new IntersectionObserver((records) => {
    inView = records[0].isIntersecting;
    updateMotion();
  });
  observer.observe(host);
  cleanups.push(() => observer.disconnect());
  updateMotion();

  function extendAxis() {
    if (axisExtended) return;
    const categories = [...originalCategories];
    while (categories.length < liveX) categories.push('');
    categories[liveX] = 'Now';
    axis.setCategories(categories, false);
    axis.update({ max: liveX + 0.5 }, false);
    axisExtended = true;
  }
  function restoreAxis() {
    if (hasReservedSlot || !axisExtended || destroying) return;
    axis.setCategories([...originalCategories], false);
    axis.update({ max: originalMax }, false);
    axisExtended = false;
  }

  function updateHover() {
    const historyHovered = hoveredContractId !== null;
    for (const entry of entries.values()) {
      if (!entry.liveSeries) continue;
      const liveHovered = !tooltip.element.hidden;
      const opacity = historyHovered && entry.contractId !== hoveredContractId ? 0.35 : 1;
      const lineWidth = entry.series.options.lineWidth ?? 2;
      const hover = entry.series.options.states?.hover;
      const hovered = historyHovered && entry.contractId === hoveredContractId;
      entry.tail?.attr({
        stroke: historyHovered ? entry.color : `url(#${uid}-${entry.contractId})`,
        'stroke-opacity': opacity,
        'stroke-width': hovered
          ? (hover?.lineWidth ?? lineWidth + (hover?.lineWidthPlus ?? 1))
          : lineWidth,
      });
      entry.marker.attr({ opacity });
      entry.ring.attr({ visibility: historyHovered || liveHovered ? 'hidden' : 'inherit' });
      entry.dot.attr({ r: liveHovered ? 4 : 2.5 });
      entry.label.attr({
        visibility: liveHovered ? 'hidden' : 'inherit',
        opacity,
      });
    }
  }

  for (const series of animating) {
    cleanups.push(
      highcharts.addEvent(series, 'afterAnimate', () => {
        animating.delete(series);
        if (!animating.size && alive()) {
          stopSweep?.();
          const ticks = [...pending.values()];
          pending.clear();
          applyTicks(ticks);
        }
      }),
    );
  }

  // Reveal the live visuals with the history's sweep, so each line runs on to Now in one motion.
  function followSweep() {
    const series = [...animating].find((s) => s.type === 'line' && s.group);
    const id = series?.group.element.getAttribute('clip-path')?.match(/#([^)"']+)/)?.[1];
    const sweep = id && Object.values(chart.sharedClips).find((clip) => clip?.id === id);
    if (!sweep) return;
    const clip = renderer.clipRect(0, 0, chart.plotLeft, chart.chartHeight);
    group.clip(clip);
    let frame = null;
    const step = () => {
      const width = chart.plotLeft + (Number(sweep.element?.getAttribute('width')) || 0);
      // Past Now, show the dot and price label whole.
      if (!sweep.element || width >= axis.toPixels(liveX)) return stopSweep();
      clip.attr({ width });
      frame = requestAnimationFrame(step);
    };
    stopSweep = () => {
      cancelAnimationFrame(frame);
      stopSweep = null;
      group.clip();
      clip.destroy();
      for (const entry of entries.values()) entry.label?.addClass('chart-live-reveal');
    };
    step();
  }

  for (const entry of entries.values()) {
    cleanups.push(
      highcharts.addEvent(entry.series, 'mouseOver', () => {
        hoveredContractId = entry.contractId;
        hideTooltip();
      }),
    );
    cleanups.push(
      highcharts.addEvent(entry.series, 'mouseOut', () => {
        if (hoveredContractId === entry.contractId) hoveredContractId = null;
        updateHover();
      }),
    );
    const syncVisibility = () => {
      entry.liveSeries?.setVisible(entry.series.visible, false);
      if (activeEntries().length) extendAxis();
      else restoreAxis();
    };
    cleanups.push(highcharts.addEvent(entry.series, 'show', syncVisibility));
    cleanups.push(highcharts.addEvent(entry.series, 'hide', syncVisibility));
  }

  function createVisual(entry, liveSeries) {
    entry.imageVisibility = entry.series.contractEndpointImage?.attr('visibility') || 'inherit';
    // A live point that's part of the chart's data stays with the chart; the sweep reveals it.
    entry.seeded = !!liveSeries;
    entry.visual = renderer.g().add(group);
    if (!entry.seeded) entry.visual.addClass('chart-live-reveal');
    if (entry.anchor) {
      entry.gradient = renderer
        .createElement('linearGradient')
        .attr({ id: `${uid}-${entry.contractId}`, gradientUnits: 'userSpaceOnUse' })
        .add(renderer.defs);
      entry.gradient.element.classList.add('chart-live-gradient');
      for (const [offset, opacity] of [
        [0, 1],
        [0.35, 0.55],
        [1, 0.25],
      ])
        renderer
          .createElement('stop')
          .attr({ offset, 'stop-color': entry.color, 'stop-opacity': opacity })
          .add(entry.gradient);
      entry.tail = renderer
        .path()
        .attr({
          stroke: `url(#${uid}-${entry.contractId})`,
          'stroke-width': 2,
          'stroke-linecap': 'round',
        })
        .addClass('chart-live-tail')
        .add(entry.visual);
    }
    entry.marker = renderer.g().add(entry.visual);
    entry.ring = renderer
      .circle(0, 0, 14)
      .attr({ fill: entry.color })
      .addClass('chart-live-ring')
      .add(entry.marker);
    entry.dot = renderer
      .circle(0, 0, 2.5)
      .attr({ fill: entry.color })
      .addClass('chart-live-dot')
      .add(entry.marker);
    entry.label = renderer.text('', 0, 0).attr({ fill: entry.color }).add(entry.visual);
    entry.liveSeries =
      liveSeries ||
      chart.addSeries(
        liveChartSeries({
          name: entry.name,
          color: entry.color,
          contractId: entry.contractId,
          x: liveX,
          price: entry.price,
          yAxis: entry.series.options.yAxis ?? 0,
          visible: entry.series.visible,
        }),
        false,
      );
  }

  // Ticks move visuals that are already on the chart with the redraw's animation.
  function place(entry, element, attrs) {
    if (renderAnimation && entry.placed) element.animate(attrs, renderAnimation);
    else element.attr(attrs);
  }

  function spreadPriceLabels(active) {
    const rows = active
      .map((entry) => {
        const box = entry.label.element.getBBox();
        const baseline = entry.label.attr('y') - box.y;
        return { entry, top: entry.labelY - baseline, height: box.height, baseline };
      })
      .sort((a, b) => a.top - b.top || a.entry.contractId - b.entry.contractId);
    let edge = chart.plotTop;
    for (const row of rows) {
      row.top = Math.max(row.top, edge);
      edge = row.top + row.height + 2;
    }
    edge = chart.plotTop + chart.plotHeight - LABEL_BASELINE_ABOVE_DOT;
    for (let i = rows.length - 1; i >= 0; i--) {
      const row = rows[i];
      row.top = Math.max(chart.plotTop, Math.min(row.top, edge - row.height));
      place(row.entry, row.entry.label, { x: row.entry.labelX, y: row.top + row.baseline });
      edge = row.top - 2;
    }
  }

  function render() {
    if (!alive()) return;
    const active = activeEntries();
    for (const entry of entries.values()) {
      const image = entry.series.contractEndpointImage;
      if (!entry.liveSeries) {
        if (image && entry.anchor)
          image.attr({
            x: axis.toPixels(entry.anchor.x) + 7,
            y: entry.series.yAxis.toPixels(entry.anchor.y) - 5,
          });
        continue;
      }
      const x = axis.toPixels(liveX),
        y = entry.series.yAxis.toPixels(entry.price),
        ay = entry.anchor && entry.series.yAxis.toPixels(entry.anchor.y);
      entry.visual.attr({ visibility: entry.series.visible ? 'inherit' : 'hidden' });
      place(entry, entry.marker, { translateX: x, translateY: y });
      if (entry.anchor) {
        const ax = axis.toPixels(entry.anchor.x);
        place(entry, entry.tail, {
          d: [
            ['M', ax, ay],
            ['L', x, y],
          ],
        });
        // Keep the bridge solid; fade only the final 28px, as in the design.
        entry.gradient.attr({ x1: Math.max(ax, x - 28), y1: 0, x2: x, y2: 0 });
        const graphic = entry.anchor.graphic;
        if (graphic) {
          if (entry.originalVisibility === undefined)
            entry.originalVisibility = graphic.attr('visibility') || 'inherit';
          graphic.hide();
        }
      }
      entry.label.attr({ text: `${entry.price}¢` });
      entry.labelX = image
        ? x - LABEL_GAP_LEFT_OF_DOT
        : Math.min(x + LABEL_GAP_RIGHT_OF_DOT, chart.chartWidth - LABEL_MAX_X_FROM_CHART_RIGHT);
      const axisLabel = entry.series.yAxis.labelGroup?.element.querySelector('text');
      entry.label.css({
        textAnchor: image ? 'end' : 'start',
        fontSize: axisLabel
          ? getComputedStyle(axisLabel).fontSize
          : entry.series.yAxis.options.labels.style.fontSize,
      });
      const noRoomAbove =
        y - LABEL_BASELINE_ABOVE_DOT - entry.label.getBBox().height < chart.plotTop;
      const tailArrivesFromAbove = image && entry.anchor && ay < y - TAIL_ANCHOR_MIN_PX_ABOVE_DOT;
      entry.labelY =
        tailArrivesFromAbove || (image && noRoomAbove)
          ? y + LABEL_BASELINE_BELOW_DOT
          : Math.max(LABEL_MIN_BASELINE_Y_FROM_CHART_TOP, y - LABEL_BASELINE_ABOVE_DOT);
      if (image) {
        image.attr({ visibility: entry.series.visible ? entry.imageVisibility : 'hidden' });
        place(entry, image, { x: x + 7, y: y - 5 });
      }
    }
    spreadPriceLabels(active);
    for (const entry of active) entry.placed = true;
    target.hidden = !active.length;
    if (!active.length) {
      hideTooltip();
      return;
    }
    const x = axis.toPixels(liveX);
    target.style.left = `${Math.max(0, Math.min(x - 22, host.clientWidth - 44))}px`;
    target.style.top = `${chart.plotTop}px`;
    target.style.height = `${chart.plotHeight}px`;
    guide.attr({ d: ['M', x, chart.plotTop, 'L', x, chart.plotTop + chart.plotHeight] });
    tooltip.update(active);
    updateHover();
    if (!tooltip.element.hidden) tooltip.position(x, chart.plotTop + 8);
  }
  cleanups.push(highcharts.addEvent(chart, 'render', render));

  function clearEntry(entry, keepSeries = false) {
    if (!entry.liveSeries) return;
    entry.visual.destroy();
    entry.gradient?.destroy();
    if (!destroying) {
      if (!keepSeries) entry.liveSeries.remove(false);
      entry.anchor?.graphic?.attr({ visibility: entry.originalVisibility ?? 'inherit' });
      entry.series.contractEndpointImage?.attr({ visibility: entry.imageVisibility });
    }
    entry.liveSeries = null;
    entry.price = null;
    entry.placed = false;
    entry.originalVisibility = undefined;
  }
  function dispose() {
    if (disposed) return;
    disposed = true;
    pending.clear();
    animating.clear();
    stopSweep?.();
    cleanups.forEach((fn) => fn());
    // A restarted overlay picks the chart's own live points back up, with their last prices.
    for (const entry of entries.values()) clearEntry(entry, entry.seeded);
    restoreAxis();
    target.remove();
    tooltip.dispose();
    group.destroy();
    host.classList.remove('chart-live-enabled', 'chart-live-reduced-motion', 'chart-live-paused');
    if (!destroying && chart.renderer) {
      chart.redraw(false);
      for (const entry of entries.values())
        if (entry.series.contractEndpointImage && entry.anchor)
          entry.series.contractEndpointImage.attr({
            x: axis.toPixels(entry.anchor.x) + 7,
            y: entry.series.yAxis.toPixels(entry.anchor.y) - 5,
          });
    }
  }
  cleanups.push(
    highcharts.addEvent(chart, 'destroy', () => {
      destroying = true;
      dispose();
    }),
  );
  function applyTicks(ticks) {
    if (!alive()) return;
    let changed = false,
      created = false;
    for (const tick of ticks) {
      const entry = entries.get(tick.contractId);
      if (
        !entry ||
        !Number.isFinite(tick.priceCents) ||
        tick.priceCents < 0 ||
        tick.priceCents > 100
      )
        continue;
      if (entry.price === tick.priceCents) continue;
      entry.price = tick.priceCents;
      if (entry.series.visible) extendAxis();
      if (!entry.liveSeries) {
        createVisual(entry);
        created = true;
      } else entry.liveSeries.data[0].update({ y: entry.price }, false, false);
      changed = true;
    }
    if (!changed) return;
    // A price move, and any axis change it causes, animates the history and live visuals together.
    renderAnimation = !created && motion() ? TICK_ANIMATION : false;
    chart.redraw(renderAnimation);
    renderAnimation = false;
  }

  // Live points drawn with the history, from the market's prices, need no first tick.
  for (const series of chart.series) {
    const entry = series.options.custom?.chartLive
      ? entries.get(Number(series.options.custom.contractId))
      : null;
    const price = series.points?.[0]?.y;
    if (!entry || entry.liveSeries || !Number.isFinite(price)) continue;
    entry.price = price;
    createVisual(entry, series);
  }
  if (activeEntries().length) {
    render();
    followSweep();
  }
  return {
    contractIds: [...entries.keys()],
    updateMany(ticks) {
      if (!alive()) return;
      if (animating.size) {
        for (const tick of ticks) pending.set(tick.contractId, tick);
        return;
      }
      applyTicks(ticks);
    },
    remove(contractId) {
      if (!alive()) return;
      pending.delete(contractId);
      const entry = entries.get(contractId);
      if (!entry?.liveSeries) return;
      clearEntry(entry);
      if (!activeEntries().length) restoreAxis();
      chart.redraw(false);
    },
    dispose,
  };
}
